import React, { useEffect } from 'react';
import { Box, CardContent, Typography } from '@mui/material';
import { alpha } from '@mui/system';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { ApplicationRoles, RoleName } from '../../../../types/user';
import { listOrganisationDailyUpdates } from '../../../../redux/slices/daily-updates';
import { formatDate } from '../../../../utils';
import { StaffInvited, StaffRolesUpdated } from '../../../../types/daily-updates';
import { ModernCard, CardHeader, COLORS } from '../../../../pages/Organisation/DailyUpdates/utils';
import OrganisationAdminSkeleton from '../../../ui/atoms/DailyUpdates/OrganisationAdminSkeleton';
import { isToday } from 'date-fns';
import { CustomStatusBadge } from './VerifierDU';

// Helper function to format time with leading zeros
const formatTime = (date: Date): string => {
	const hours = date.getHours().toString().padStart(2, '0');
	const minutes = date.getMinutes().toString().padStart(2, '0');
	return `${hours}:${minutes}`;
};

const OrganisationAdminDU = () => {
	const dispatch = useAppDispatch();

	const [organisation, user] = useAppSelector((state) => [state.organisation.current, state.user.currentUser]);
	const organisationAdminDU = useAppSelector(
		(state) => state.dailyUpdates.dailyUpdates?.[RoleName.OrgnaisationAdmin]
	);

	const organisationId = organisation?.id || user?.roles?.find((r) => r.organisationId)?.organisationId;

	useEffect(() => {
		organisationId &&
			user?.id &&
			dispatch(
				listOrganisationDailyUpdates({
					organisationId,
					roleName: RoleName.OrgnaisationAdmin,
					status: null,
					dailyUpdates: true,
					lastLoggedOut: localStorage.getItem(`lastLogout_${user?.id}`) || undefined
				})
			);
	}, [organisationId, user?.id, dispatch]);

	if (!organisationAdminDU) {
		return <OrganisationAdminSkeleton />;
	}

	const { newStaffInvited, staffRolesUpdated } = organisationAdminDU;

	return (
		<Box
			sx={{
				p: 3,
				maxWidth: '1400px',
				margin: '0 auto',
				backgroundColor: alpha(COLORS.neutral, 0.5),
				borderRadius: '24px'
			}}
		>
			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				{/* New Verification Requests Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							New Verification Requests
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<CustomStatusBadge color={COLORS.success} label={organisationAdminDU.newVerificationRequests} />
					</CardContent>
				</ModernCard>

				{/* Pending Verification Requests Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Pending Verification Requests
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
						<CustomStatusBadge
							color={COLORS.warning}
							label={organisationAdminDU.pendingVerificationRequests}
						/>
					</CardContent>
				</ModernCard>
			</Box>

			{/* Staff Invited Card */}
			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Staff Invited
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{newStaffInvited.length} recent invitations
						</Typography>
					</CardHeader>
					{/* eslint-disable no-mixed-spaces-and-tabs */}
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{newStaffInvited.length > 0 ? (
								newStaffInvited.map((staff: StaffInvited, index: number) => (
									<Box
										key={index}
										sx={{ p: 2, borderRadius: '8px', backgroundColor: alpha(COLORS.neutral, 0.6) }}
									>
										<Typography variant="body1" sx={{ fontWeight: 500, color: COLORS.text }}>
											{staff.email} was invited as{' '}
											{
												ApplicationRoles[
													staff.roles.find(
														(role) => role !== RoleName.Employee
													) as keyof typeof ApplicationRoles
												]
											}
										</Typography>
										<Typography variant="caption" sx={{ color: COLORS.textLight }}>
											{isToday(new Date(staff.creationDateTime))
												? `Today at ${formatTime(new Date(staff.creationDateTime))}`
												: `${formatDate(staff.creationDateTime)} ${formatTime(
													new Date(staff.creationDateTime)
												  )}`}
										</Typography>
									</Box>
								))
							) : (
								<Typography
									variant="body2"
									sx={{ color: COLORS.textLight, textAlign: 'center', py: 2 }}
								>
									No recent staff invitations
								</Typography>
							)}
						</Box>
					</CardContent>
				</ModernCard>
			</Box>

			{/* Staff Role Updates Card */}
			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Staff Role Updates
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{staffRolesUpdated.length} recent role changes
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{staffRolesUpdated.length > 0 ? (
								staffRolesUpdated.map((staff: StaffRolesUpdated, index: number) => (
									<Box
										key={index}
										sx={{ p: 2, borderRadius: '8px', backgroundColor: alpha(COLORS.neutral, 0.6) }}
									>
										<Typography
											variant="body1"
											sx={{
												fontWeight: 500,
												color: COLORS.text,
												mb: 1,
												display: 'flex',
												alignItems: 'center',
												flexDirection: 'row'
											}}
										>
											{staff.user.name ? (
												<Typography
													variant="body1"
													sx={{ fontWeight: 500, color: COLORS.text }}
												>
													{staff.user.name} &apos;s roles were changed
												</Typography>
											) : (
												<Typography
													variant="body1"
													sx={{ fontWeight: 500, color: COLORS.text }}
												>
													{staff.user.email}
												</Typography>
											)}

											<Typography
												component="span"
												variant="body2"
												sx={{ color: COLORS.textLight, ml: 1 }}
											>
												{staff.user.name && staff.user.email
													? `(${staff.user.email})`
													: '(New User)'}
											</Typography>
										</Typography>

										{staff.roles.length > 0 && (
											<>
												<Typography variant="body2" sx={{ mb: 1 }}>
													Roles {staff.action === 'create' ? 'added' : 'removed'}:
												</Typography>
												<Box sx={{ pl: 2 }}>
													{staff.roles.map((role, roleIdx) => (
														<Box
															key={roleIdx}
															sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}
														>
															<Box
																component="span"
																sx={{
																	width: '6px',
																	height: '6px',
																	borderRadius: '50%',
																	backgroundColor:
																		staff.action === 'create'
																			? COLORS.success
																			: COLORS.error,
																	mr: 1
																}}
															/>
															<Typography variant="body2">
																{
																	ApplicationRoles[
																		role as keyof typeof ApplicationRoles
																	]
																}
															</Typography>
														</Box>
													))}
												</Box>
											</>
										)}

										<Typography
											variant="caption"
											sx={{ color: COLORS.textLight, display: 'block', mt: 1 }}
										>
											{isToday(new Date(staff.creationDateTime))
												? `Today at ${formatTime(new Date(staff.creationDateTime))}`
												: `${formatDate(staff.creationDateTime)} ${formatTime(
													new Date(staff.creationDateTime)
												  )}`}
										</Typography>
									</Box>
								))
							) : (
								<Typography
									variant="body2"
									sx={{ color: COLORS.textLight, textAlign: 'center', py: 2 }}
								>
									No recent role updates
								</Typography>
							)}
						</Box>
					</CardContent>
				</ModernCard>
				{/* eslint-enable no-mixed-spaces-and-tabs */}
			</Box>
		</Box>
	);
};

export default OrganisationAdminDU;
